import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { APP_CONFIG } from './config/app';
import { logFunctionCall } from './utils/analytics';

const firebaseConfig = {
  apiKey: "AIzaSyAiyL1zXqKEBoUxUKIEyj0s1XzW-TlCLd4",
  authDomain: "entwinedworkspaceadmin.firebaseapp.com",
  projectId: "entwinedworkspaceadmin",
  storageBucket: "entwinedworkspaceadmin.firebasestorage.app",
  messagingSenderId: "1042541785633",
  appId: "1:1042541785633:web:34f0fe95dd6d43aa2f2960"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');

// Google Auth Provider with Admin SDK scopes and OAuth client ID
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  client_id: '1042541785633-45enrci3utddf35bnc1qss0em2iasv40.apps.googleusercontent.com',
  prompt: 'select_account' // Force account selection to allow testing different users
});

// Add scopes based on configuration
if (APP_CONFIG.INTERNAL_MODE) {
  // For internal mode, use minimal scopes
  googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
  googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
} else {
  // For full verification mode, use admin scopes
  googleProvider.addScope('https://www.googleapis.com/auth/admin.directory.user');
  googleProvider.addScope('https://www.googleapis.com/auth/admin.directory.orgunit');
  googleProvider.addScope('https://www.googleapis.com/auth/admin.directory.rolemanagement');
  googleProvider.addScope('https://www.googleapis.com/auth/admin.directory.customer');
  googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
  googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
}

// Custom sign-in function that ensures we get the right scopes
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    // Force token refresh to ensure we have the latest scopes
    await result.user.getIdToken(true);
    
    return result;
  } catch (error) {
    console.error('Sign-in error:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    // Sign out from Firebase
    await signOut(auth);
    
    // Clear any cached OAuth data from localStorage/sessionStorage
    // This helps prevent Google from auto-selecting the previous account
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('firebase') || key.includes('google') || key.includes('oauth'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Also clear sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('firebase') || key.includes('google') || key.includes('oauth'))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    console.log('✅ Signed out and cleared auth cache');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Helper function to wrap Firebase callable functions with automatic logging
const createLoggedFunction = (functionName, callableFunction) => {
  return async (data) => {
    const startTime = Date.now();
    let success = false;
    let error = null;
    
    try {
      const result = await callableFunction(data);
      success = true;
      const duration = Date.now() - startTime;
      logFunctionCall(functionName, true, duration);
      return result;
    } catch (err) {
      success = false;
      error = err;
      const duration = Date.now() - startTime;
      logFunctionCall(functionName, false, duration, error);
      throw err;
    }
  };
};

// Cloud Functions - wrapped with automatic logging
const _getOrganizationalUnits = httpsCallable(functions, 'getOrganizationalUnits');
const _getUsers = httpsCallable(functions, 'getUsers');
const _assignRole = httpsCallable(functions, 'assignRole');
const _createOrganizationalUnit = httpsCallable(functions, 'createOrganizationalUnit');
const _deleteOrganizationalUnit = httpsCallable(functions, 'deleteOrganizationalUnit');
const _getRoleAssignments = httpsCallable(functions, 'getRoleAssignments');
const _getAvailableRoles = httpsCallable(functions, 'getAvailableRoles');
const _createUser = httpsCallable(functions, 'createUser');
const _deleteUser = httpsCallable(functions, 'deleteUser');
const _updateUser = httpsCallable(functions, 'updateUser');
const _submitAccessRequest = httpsCallable(functions, 'submitAccessRequest');
const _getPendingAccessRequests = httpsCallable(functions, 'getPendingAccessRequests');
const _getApprovedAccessRequests = httpsCallable(functions, 'getApprovedAccessRequests');
const _updateAccessRequestStatus = httpsCallable(functions, 'updateAccessRequestStatus');
const _updateRequestStatusesFromUsers = httpsCallable(functions, 'updateRequestStatusesFromUsers');
const _checkUserExists = httpsCallable(functions, 'checkUserExists');
const _testFirestore = httpsCallable(functions, 'testFirestore');
const _testDomainSetup = httpsCallable(functions, 'testDomainSetup');
const _getDelegationInfo = httpsCallable(functions, 'getDelegationInfo');
const _refreshCache = httpsCallable(functions, 'refreshCache');
const _getChromeDeviceCounts = httpsCallable(functions, 'getChromeDeviceCounts');
const _testRoleAssignment = httpsCallable(functions, 'testRoleAssignment');
const _testChromeDeviceAPI = httpsCallable(functions, 'testChromeDeviceAPI');
const _getUsersWithRoles = httpsCallable(functions, 'getUsersWithRoles');
const _getServicesAndPrivileges = httpsCallable(functions, 'getServicesAndPrivileges');
const _createCustomRoles = httpsCallable(functions, 'createCustomRoles');
const _getRoleConfiguration = httpsCallable(functions, 'getRoleConfiguration');
const _saveRoleConfiguration = httpsCallable(functions, 'saveRoleConfiguration');
const _submitRoleRequest = httpsCallable(functions, 'submitRoleRequest');
const _getPendingRoleRequests = httpsCallable(functions, 'getPendingRoleRequests');
const _getApprovedRoleRequests = httpsCallable(functions, 'getApprovedRoleRequests');
const _updateRoleRequestStatus = httpsCallable(functions, 'updateRoleRequestStatus');
const _logUsage = httpsCallable(functions, 'logUsage');
const _getUsageAnalytics = httpsCallable(functions, 'getUsageAnalytics');
const _getManageableQueues = httpsCallable(functions, 'getManageableQueues');
const _getOUApprovers = httpsCallable(functions, 'getOUApprovers');
const _getPrimaryOUs = httpsCallable(functions, 'getPrimaryOUs');
const _getCustomerAdmins = httpsCallable(functions, 'getCustomerAdmins');
const _setCustomerAdmins = httpsCallable(functions, 'setCustomerAdmins');
const _getAllCustomerAdmins = httpsCallable(functions, 'getAllCustomerAdmins');

// Export wrapped functions with automatic logging
// Note: logUsage is not wrapped to avoid infinite recursion
export const getOrganizationalUnits = createLoggedFunction('getOrganizationalUnits', _getOrganizationalUnits);
export const getUsers = createLoggedFunction('getUsers', _getUsers);
export const assignRole = createLoggedFunction('assignRole', _assignRole);
export const createOrganizationalUnit = createLoggedFunction('createOrganizationalUnit', _createOrganizationalUnit);
export const deleteOrganizationalUnit = createLoggedFunction('deleteOrganizationalUnit', _deleteOrganizationalUnit);
export const getRoleAssignments = createLoggedFunction('getRoleAssignments', _getRoleAssignments);
export const getAvailableRoles = createLoggedFunction('getAvailableRoles', _getAvailableRoles);
export const createUser = createLoggedFunction('createUser', _createUser);
export const deleteUser = createLoggedFunction('deleteUser', _deleteUser);
export const updateUser = createLoggedFunction('updateUser', _updateUser);
export const submitAccessRequest = createLoggedFunction('submitAccessRequest', _submitAccessRequest);
export const getPendingAccessRequests = createLoggedFunction('getPendingAccessRequests', _getPendingAccessRequests);
export const getApprovedAccessRequests = createLoggedFunction('getApprovedAccessRequests', _getApprovedAccessRequests);
export const updateAccessRequestStatus = createLoggedFunction('updateAccessRequestStatus', _updateAccessRequestStatus);
export const updateRequestStatusesFromUsers = createLoggedFunction('updateRequestStatusesFromUsers', _updateRequestStatusesFromUsers);
export const checkUserExists = createLoggedFunction('checkUserExists', _checkUserExists);
export const testFirestore = createLoggedFunction('testFirestore', _testFirestore);
export const testDomainSetup = createLoggedFunction('testDomainSetup', _testDomainSetup);
export const getDelegationInfo = createLoggedFunction('getDelegationInfo', _getDelegationInfo);
export const refreshCache = createLoggedFunction('refreshCache', _refreshCache);
export const getChromeDeviceCounts = createLoggedFunction('getChromeDeviceCounts', _getChromeDeviceCounts);
export const testRoleAssignment = createLoggedFunction('testRoleAssignment', _testRoleAssignment);
export const testChromeDeviceAPI = createLoggedFunction('testChromeDeviceAPI', _testChromeDeviceAPI);
export const getUsersWithRoles = createLoggedFunction('getUsersWithRoles', _getUsersWithRoles);
export const getServicesAndPrivileges = createLoggedFunction('getServicesAndPrivileges', _getServicesAndPrivileges);
export const createCustomRoles = createLoggedFunction('createCustomRoles', _createCustomRoles);
export const getRoleConfiguration = createLoggedFunction('getRoleConfiguration', _getRoleConfiguration);
export const saveRoleConfiguration = createLoggedFunction('saveRoleConfiguration', _saveRoleConfiguration);
export const submitRoleRequest = createLoggedFunction('submitRoleRequest', _submitRoleRequest);
export const getPendingRoleRequests = createLoggedFunction('getPendingRoleRequests', _getPendingRoleRequests);
export const getApprovedRoleRequests = createLoggedFunction('getApprovedRoleRequests', _getApprovedRoleRequests);
export const updateRoleRequestStatus = createLoggedFunction('updateRoleRequestStatus', _updateRoleRequestStatus);
export const logUsage = _logUsage; // Not wrapped to avoid infinite recursion
export const getUsageAnalytics = createLoggedFunction('getUsageAnalytics', _getUsageAnalytics);
export const getManageableQueues = createLoggedFunction('getManageableQueues', _getManageableQueues);
export const getOUApprovers = createLoggedFunction('getOUApprovers', _getOUApprovers);
export const getPrimaryOUs = createLoggedFunction('getPrimaryOUs', _getPrimaryOUs);
export const getCustomerAdmins = createLoggedFunction('getCustomerAdmins', _getCustomerAdmins);
export const setCustomerAdmins = createLoggedFunction('setCustomerAdmins', _setCustomerAdmins);
export const getAllCustomerAdmins = createLoggedFunction('getAllCustomerAdmins', _getAllCustomerAdmins);

export { auth, db, functions };
export default app; 